import {forkJoin, Observable, of, Subject} from 'rxjs';
import {debounceTime, map, switchMap} from 'rxjs/operators';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Equipment, Favourite} from '@app/graphql-typings/graphql';
import {EquipmentGroup} from '../equipment-group.component';
import {Subscription} from 'rxjs';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {FavouritesCache} from '@shared/data-stores/favourites-cache';
import {TasksCache} from '@shared/data-stores/tasks-cache';
import {Task} from "@shared/services/api/task.interface";

export interface IAlert {
  type: string;
  message: {
    messageKey: string;
    parameters?: {
      [key: string]: any;
    };
  };
}

export interface EquipmentGroupData extends EquipmentGroup {
  options: {
    favourites: Favourite[];
  };
}

interface ValidatedEquipmentGroupData extends EquipmentGroupData {
  valid?: boolean;
}

@Component({
  selector: 'app-copy-equipment-group',
  templateUrl: './copy-equipment-group.component.html',
  styleUrls: ['./copy-equipment-group.component.scss'],
  standalone: false
})
export class CopyEquipmentGroupComponent implements OnInit, OnDestroy {

  @Input() equipment: Equipment[];
  @Input() selectedEquipment: Equipment;
  @Input() selectedFavourites: Favourite[];
  @Input() selectedTasks: Task[];

  readonly MAX_EQUIPMENT_FOR_SELECT_ALL = 25;
  chosenEquipment: Equipment[];
  checking = false;
  loading = false;
  atLeastOneValidEquipment = false;
  alerts: Array<IAlert> = [];

  private readonly logger: AceLogger;
  private readonly EQUIPMENT_DEBOUNCE_TIME = 1000;
  private selectedMetadataIds: Set<number>;
  private selectedTemplateIds: Set<string>;
  private equipmentData: Map<number, ValidatedEquipmentGroupData>;
  private ngUnsubscribe = new Subject<void>();
  private equipmentChanged$: Subject<EquipmentGroup[]> = new Subject();
  private equipmentChangedSubscription: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private favouritesCache: FavouritesCache,
    private tasksCache: TasksCache,
    logService: AceLoggerService
  ) {
    this.logger = logService.create(CopyEquipmentGroupComponent.name);
  }

  ngOnInit() {
    this.selectedMetadataIds = new Set(this.selectedFavourites ? this.selectedFavourites.map(f => f.metadata_id) : []);
    this.selectedTemplateIds = new Set(this.selectedTasks ? this.selectedTasks.map(t => t.template.template_id) : []);
    this.equipmentData = new Map();

    this.equipmentChangedSubscription =  this.equipmentChanged$.pipe(
      debounceTime(this.EQUIPMENT_DEBOUNCE_TIME),
      switchMap(_ => {
        this.checking = true;

        // Find chosen equipment without checks
        if (this.chosenEquipment && this.chosenEquipment.length) {
          const uncheckedEquipment = this.chosenEquipment.filter(e => !this.equipmentData.has(e.equipment_id));
          return this.checkAddPopulatedControls(uncheckedEquipment, this.selectedMetadataIds, this.selectedTemplateIds);
        } else {
          return of(new Map());
        }
    })).subscribe((data: Map<number, ValidatedEquipmentGroupData>) => {
      data.forEach((d, equipmentId) => {
        this.equipmentData.set(equipmentId, d);
      });
      this.processChecks();
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.equipmentChangedSubscription) {
      this.equipmentChangedSubscription.unsubscribe();
    }
  }

  onChange() {
    this.equipmentChanged$.next([]);
  }

  onSubmit() {
    this.loading = true;
    const data: EquipmentGroupData[] = [];
    this.chosenEquipment.forEach(equipment => {
      const equipmentId = equipment.equipment_id;
      if (this.equipmentData.has(equipmentId)) {
        const d: ValidatedEquipmentGroupData = this.equipmentData.get(equipmentId);
        if (d.valid !== undefined && d.valid !== null) {
          if (d.valid) {
            data.push({
              equipment: equipment,
              favourites: d.favourites ? d.favourites : [],
              tasks: d.tasks ? d.tasks : [],
              functionParamsAndChartRawValues: null,
              options: {
                favourites: d.options.favourites
              }
            });
          } else {
            this.logger.debug('Not copying equipment as it was invalid. Equipment:', equipment);
          }
        } else {
          this.logger.error('Found equipment which was unvalidated', equipment);
        }
      } else {
        this.logger.error('Found equipment which was unvalidated', equipment);
      }
    });

    this.loading = false;
    this.activeModal.close(data);
  }

  selectAll(_) {
    this.chosenEquipment = this.equipment;
    this.onChange();
  }

  private processChecks() {
    const alerts: IAlert[] = [];

    if (this.chosenEquipment && this.chosenEquipment.length) {
      const problemEquipment: Equipment[] = [];
      let atLeastOneValidEquipment = false;

      this.chosenEquipment.forEach(equipment => {
        if (this.equipmentData.has(equipment.equipment_id)) {
          const d: ValidatedEquipmentGroupData = this.equipmentData.get(equipment.equipment_id);
          if (d.valid === undefined || d.valid === null) {
            d.valid = d.favourites && d.favourites.length > 0;
          }

          if (!d.valid) {
            problemEquipment.push(equipment);
          }

          atLeastOneValidEquipment = atLeastOneValidEquipment || d.valid;
        }
      });

      this.atLeastOneValidEquipment = atLeastOneValidEquipment;

      if (problemEquipment.length) {
        if (atLeastOneValidEquipment) {
          const warningEquipment: string = problemEquipment.map(e => e.name).join(', ');
          alerts.push({type: 'warning', message: {messageKey: 'No Favourites or Tasks exist for', parameters: {equipment: warningEquipment}}});
        } else {
          alerts.push({type: 'danger', message: {messageKey: 'No Favourites or Tasks exist for given Equipment'}});
        }
      }
    } else {
      this.atLeastOneValidEquipment = false;
    }

    this.alerts = alerts;
    this.checking = false;
  }

  private checkAddPopulatedControls(equipment: Equipment[], metadataIds: Set<number>, templateIds: Set<string>): Observable<Map<number, ValidatedEquipmentGroupData>> {
    const equipmentIds: number[] = Array.from(new Set(equipment.map(e => e.equipment_id)));
    const favouritesByEquipmentId$ = this.favouritesCache.getFavouritesForEquipmentIds(equipmentIds);
    const tasksByEquipmentId$ = this.tasksCache.getTasksForEquipmentIds(equipmentIds);
    return forkJoin([favouritesByEquipmentId$, tasksByEquipmentId$]).pipe(map(([favouritesByEquipmentId, tasksByEquipmentId]) => {
      const data: Map<number, ValidatedEquipmentGroupData> = new Map();
      equipment.forEach(e => {
        const equipmentId = e.equipment_id;
        const equipmentFavourites: Favourite[] = favouritesByEquipmentId.get(equipmentId) ?? [];
        const sameMetadataIdFavourites: Favourite[] = equipmentFavourites.filter(f => metadataIds.has(f.metadata_id));
        const equipmentTasks = tasksByEquipmentId.has(equipmentId) ? tasksByEquipmentId.get(equipmentId) : [];
        const sameTemplateIdTasks = equipmentTasks.filter(t => templateIds.has(t.template.template_id));
        data.set(equipmentId, {
          equipment: e,
          favourites: sameMetadataIdFavourites,
          tasks: sameTemplateIdTasks,
          options: {
            favourites: equipmentFavourites
          }
        });
      });

      return data;
    }));
  }
}
